import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import { BrandFooter } from '../components/ui/BrandFooter';
import { BrandMark } from '../components/ui/BrandMark';
import { cssColor } from '../theme/engine';
import { Button } from '../components/ui/Button';
import { TextField } from '../components/ui/Field';

type FormValues = {
  password: string;
  confirmPassword: string;
};

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('hash'); 
  const auth = (useAuth() as any) || {};

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [isValidHash, setIsValidHash] = useState<boolean | null>(null); 
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [accountPreview, setAccountPreview] = useState<string | null>(null); 

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ mode: 'onBlur' });

  const password = watch('password', '');

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setIsValidHash(false);
      return;
    }

    let mounted = true;
    const verify = async () => {
      setVerifying(true);
      setVerifyError(null);
      setAccountPreview(null);

      try {
        const resp: any = await authService.checkHash({ secretHash: token });
        if (mounted) {
          setIsValidHash(true);
          const email =
            resp?.valid?.email 


            console.log(email,"Email in verify hash")
          if (email && typeof email === 'string') setAccountPreview(email);
        }
      } catch (err: any) {
        console.error('checkHash error', err);
        if (mounted) {
          setIsValidHash(false);
          setVerifyError(err?.message || 'Reset link invalid or expired');
        }
      } finally {
        if (mounted) setVerifying(false);
      }
    };

    verify();

    return () => {
      mounted = false;
    };
  }, [token]);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-agentic-white-off p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow p-6 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>

          <h2 className="text-lg font-semibold text-gray-900 mb-2">Invalid or missing reset link</h2>
          <p className="text-sm text-gray-600 mb-6">
            The password reset link is invalid or expired. Please request a new password reset from the sign-in page.
          </p>

          <div className="space-y-3">
            <Button tone="custom"
              onClick={() => navigate('/forgot-password')}
              className="w-full py-3 rounded-lg border border-gray-200 text-gray-800 font-medium"
            >
              Request new reset link
            </Button>

            <Button tone="primary"
              onClick={() => navigate('/login')}
              className="w-full py-3 rounded-lg font-semibold"
            >
              Back to sign in
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-agentic-white-off p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow p-6 text-center">
          <div className="mb-3">Verifying reset link...</div>
          <div className="h-2 bg-gray-100 rounded overflow-hidden">
            <div className="h-full w-1/3 animate-pulse bg-agentic-primary-blue" />
          </div>
        </div>
      </div>
    );
  }

  if (isValidHash === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-agentic-white-off p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow p-6 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>

          <h2 className="text-lg font-semibold text-gray-900 mb-2">Invalid or expired reset link</h2>
          <p className="text-sm text-gray-600 mb-4">{verifyError || 'The password reset link is invalid or expired.'}</p>

          <div className="space-y-3">
            <Button tone="custom"
              onClick={() => navigate('/forgot-password')}
              className="w-full py-3 rounded-lg border border-gray-200 text-gray-800 font-medium"
            >
              Request new reset link
            </Button>

            <Button tone="primary"
              onClick={() => navigate('/login')}
              className="w-full py-3 rounded-lg font-semibold"
            >
              Back to sign in
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const onSubmit = async (values: FormValues) => {
    if (values.password !== values.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      await authService.setPasswordWithCheckHash({
        secretHash: token,
        newPassword: values.password,
        confirmPassword: values.confirmPassword,
      });

      toast.success('Password reset successful');
      setIsSubmitted(true);
    } catch (err: any) {
      console.error('Reset password error', err);
      toast.error(err?.message || 'Failed to reset password');
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-agentic-white-off p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow p-6 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h2 className="text-lg font-semibold text-gray-900 mb-2">Password updated</h2>
          <p className="text-sm text-gray-600 mb-6">
            Your password has been updated successfully. Use your new password to sign in.
          </p>

          <Button tone="primary"
            onClick={() => navigate('/login')}
            className="w-full py-3 rounded-lg font-semibold"
          >
            Sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-agentic-white-off">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* Left Panel (visuals) */}
        <div className="hidden lg:flex relative p-8">
          <div className="relative w-full rounded-3xl overflow-hidden bg-gradient-to-br from-blue-200 via-blue-300 to-blue-400">
            <div className="absolute inset-0 opacity-70">
              <div className="absolute -top-10 -left-10 w-80 h-80 bg-white/30 rounded-full blur-3xl" />
              <div className="absolute bottom-10 right-10 w-96 h-96 bg-white/20 rounded-full blur-3xl" />
            </div>

            <div className="relative p-10 space-y-8">
              <div className="max-w-xl">
                <h2 className="text-3xl font-semibold text-gray-800 leading-tight">
                  Orchestrate your Agentic AI workforce
                </h2>
                <p className="mt-3 text-gray-700">
                  Coordinate autonomous agents across claims, underwriting, fraud, and support — securely, at enterprise
                  scale.
                </p>
              </div>

              <div className="space-y-6 max-w-md">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-md">
                  <div className="text-xs font-semibold text-gray-500 tracking-wide">AGENTS ONLINE</div>
                  <div className="mt-2 flex items-end space-x-2">
                    <div className="text-4xl font-bold text-gray-900">18</div>
                    <div className="text-xs text-gray-500 mb-2">live</div>
                  </div>
                  <p className="mt-2 text-sm text-gray-600">Health across OCR, Customer Support, Compliance, Fraud, and Reporting agents</p>
                  <div className="mt-4 flex items-end space-x-2 h-24">
                    <div className="w-6 bg-green-200 h-6 rounded" />
                    <div className="w-6 bg-green-300 h-10 rounded" />
                    <div className="w-6 bg-green-400 h-12 rounded" />
                    <div className="w-6 bg-green-500 h-20 rounded" />
                  </div>
                </div>

                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-md">
                  <div className="text-xs font-semibold text-gray-500 tracking-wide">AUTOMATIONS EXECUTED</div>
                  <div className="mt-2 flex items-end space-x-2">
                    <div className="text-4xl font-bold text-gray-900">26.8K</div>
                    <div className="text-xs text-gray-500 mb-2">last month</div>
                  </div>
                  <p className="mt-2 text-sm text-gray-600">Automated triage, enrichment, and decisioning across enterprise workflows</p>
                  <div className="mt-4 flex items-end space-x-2 h-24">
                    <div className="w-6 bg-blue-200 h-10 rounded" />
                    <div className="w-6 bg-blue-300 h-16 rounded" />
                    <div className="w-6 bg-blue-400 h-20 rounded" />
                    <div className="w-6 bg-blue-500 h-24 rounded" />
                  </div>
                </div>
              </div>

              <div className="max-w-md mt-6">
                <div className="bg-white/70 rounded-2xl p-6 shadow-inner">
                  <svg className="w-full h-40" viewBox="0 0 400 160">
                    <defs>
                      <linearGradient id="node" x1="0" x2="1">
                        <stop offset="0%" style={{ stopColor: cssColor('93c5fd') }} />
                        <stop offset="100%" style={{ stopColor: cssColor('3b82f6') }} />
                      </linearGradient>
                    </defs>
                    <g style={{ stroke: cssColor('93c5fd') }} strokeWidth="2" opacity="0.8">
                      <line x1="60" y1="80" x2="160" y2="40" />
                      <line x1="60" y1="80" x2="160" y2="120" />
                      <line x1="160" y1="40" x2="300" y2="80" />
                      <line x1="160" y1="120" x2="300" y2="80" />
                    </g>
                    <g>
                      <circle cx="60" cy="80" r="12" fill="url(#node)" />
                      <rect x="152" y="32" width="16" height="16" rx="3" fill="url(#node)" />
                      <rect x="152" y="112" width="16" height="16" rx="3" fill="url(#node)" />
                      <circle cx="300" cy="80" r="12" fill="url(#node)" />
                    </g>
                  </svg>
                  <div className="text-xs text-gray-600 text-center">Autonomous agents collaborating across your enterprise graph</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Reset Password Form */}
        <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
          <div className="w-full max-w-md">
            <div className="flex items-center mb-6">
              <BrandMark className="h-16 w-auto" />
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-1">Reset password</h1>
            <p className="text-gray-600 mb-2">
              Provide a new secure password associated with your account.
            </p>

            {/* show verified account preview if available */}
            {accountPreview && (
              <div className="mb-4 text-sm text-gray-700 bg-green-50 p-2 rounded">
                Resetting password for <strong>{accountPreview}</strong>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
              <div className="flex flex-col text-sm">
                <label className="text-gray-600 text-xs font-medium mb-2">New password</label>
                <TextField
                  {...register('password', {
                    required: 'New password is required',
                    minLength: { value: 8, message: 'Password must be at least 8 characters' },
                  })}
                  type="password"
                  className="mt-1 p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
                  placeholder="NewSecurePass123!"
                  autoComplete="new-password"
                />
                {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password.message}</p>}
              </div>

              <div className="flex flex-col text-sm">
                <label className="text-gray-600 text-xs font-medium mb-2">Confirm new password</label>
                <TextField
                  {...register('confirmPassword', {
                    required: 'Please confirm your new password',
                    validate: value => value === password || 'Passwords do not match',
                  })}
                  type="password"
                  className="mt-1 p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
                  placeholder="NewSecurePass123!"
                  autoComplete="new-password"
                />
                {errors.confirmPassword && <p className="text-xs text-red-600 mt-1">{errors.confirmPassword.message}</p>}
              </div>

              <div>
                <Button tone="primary"
                  type="submit"
                  disabled={isSubmitting || verifying}
                  className="w-full py-3 rounded-lg font-semibold disabled:opacity-60"
                >
                  {isSubmitting ? 'Updating...' : 'Update password'}
                </Button>
              </div>

              <div className="text-center text-sm text-gray-500">
                Remembered your password?{' '}
                <Button tone="custom" onClick={() => navigate('/login')} type="button" className="text-agentic-primary-blue hover:text-agentic-primary-blue-dark font-medium">
                  Sign in
                </Button>
              </div>
            </form>

            <BrandFooter />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
